import { type ReactNode } from 'react';
import { useImpressionRef } from '@zep/logger/react';
import { trackEvent } from '@zep/logger/tracking';
import { useIsLoggerUserInfoSet } from '@/lib/auth/UserContext';
import { AiGenerateEntryCardsUI } from '@/views/build/features/ai-builder-chat/components/AiGenerateEntryCardsUI';
import { useAiGenerateDockCopy } from '@/views/build/features/ai-builder-chat/hooks/useAiGenerateDockCopy';
import { useRequestAiGenerateDock } from '@/views/build/features/ai-builder-chat/hooks/useRequestAiGenerateDock';
import { useQuizList } from './useQuizList';

/**
 * 빌드 화면 위 AI 생성 진입 카드 — 주제·자료 카드는 아래 생성기 입력창을 그 탭으로 연다(자료는 파일 탭).
 * 채팅은 문제 카드 요술봉에서 그대로 열린다. AI 를 쓸 수 있는지는 호출부가 정한다.
 */
export const BuildAiEntryCards = ({
  layoutMode = 'page',
  hasQuizBankEntry = false
}: {
  layoutMode?: 'page' | 'ai-shell';
  /** 아래에 문제 은행 카드가 함께 놓이는지. 없으면 주제·자료 두 장만 남으니 문제가 있어도 크게 그린다(10/23 지민). */
  hasQuizBankEntry?: boolean;
}) => {
  const {
    quizList
  } = useQuizList();
  const {
    entryCopy
  } = useAiGenerateDockCopy();
  const requestAiGenerateDock = useRequestAiGenerateDock();
  // 문제 은행 카드가 없으면 주제·자료 두 장만 남으니 문제가 있어도 크게 그린다.
  const size = quizList.length === 0 || !hasQuizBankEntry ? 'large' : 'small';
  // 빈 퀴즈에 문제 은행 카드도 없으면 두 장이 화면 가운데를 채우게 더 높인다(Figma 41092:29118). 문제가 있으면 목록을 밀지 않게 그대로.
  const tall = quizList.length === 0 && !hasQuizBankEntry;
  return <AiGenerateEntryImpression size={size} quizCount={quizList.length} data-sentry-element="AiGenerateEntryImpression" data-sentry-component="BuildAiEntryCards" data-sentry-source-file="BuildAiEntryCards.tsx">
      <AiGenerateEntryCardsUI copy={entryCopy} size={size} tall={tall} layoutMode={layoutMode} onSelectTopic={() => requestAiGenerateDock('query', 'entry_card')} onSelectSource={() => requestAiGenerateDock('file', 'entry_card')} data-sentry-element="AiGenerateEntryCardsUI" data-sentry-source-file="BuildAiEntryCards.tsx" />
    </AiGenerateEntryImpression>;
};

/*
  유저 문맥이 실린 뒤에만 관측을 건다 — 노출은 첫 교차에서 한 번만 쏘는데, 자식 이펙트가
  UserProvider 보다 먼저 돌아 게이트 없이 걸면 이 분모만 사용자·플랜 문맥 없이 저장된다.
*/
const AiGenerateEntryImpression = ({
  size,
  quizCount,
  children
}: {
  size: 'large' | 'small';
  quizCount: number;
  children: ReactNode;
}) => {
  const isLoggerUserInfoSet = useIsLoggerUserInfoSet();
  if (!isLoggerUserInfoSet) return <div>{children}</div>;
  return <AiGenerateEntryObserved size={size} quizCount={quizCount} data-sentry-element="AiGenerateEntryObserved" data-sentry-component="AiGenerateEntryImpression" data-sentry-source-file="BuildAiEntryCards.tsx">
      {children}
    </AiGenerateEntryObserved>;
};
const AiGenerateEntryObserved = ({
  size,
  quizCount,
  children
}: {
  size: 'large' | 'small';
  quizCount: number;
  children: ReactNode;
}) => {
  const impressionRef = useImpressionRef<HTMLDivElement>(() => trackEvent('quiz_ai_generator_entry_view', {
    place: 'entry_card',
    size,
    quiz_count: quizCount
  }));
  return <div ref={impressionRef} data-sentry-component="AiGenerateEntryObserved" data-sentry-source-file="BuildAiEntryCards.tsx">{children}</div>;
};